import { useEffect, useId, type ReactNode } from 'react';
import { useFocusTrap } from '../../hooks/useFocusManagement';
import { Card } from './Card';

export type ModalSize = 'sm' | 'md' | 'lg';

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
};

interface ModalProps {
  /** Whether the modal is currently open. Renders `null` when false. */
  open: boolean;
  /** Fires when the user presses ESC or clicks the backdrop. */
  onClose: () => void;
  /** Accessible label announced by screen readers. Falls back to ariaLabelledBy. */
  ariaLabel?: string;
  /** Element id whose text labels the dialog (alternative to ariaLabel). */
  ariaLabelledBy?: string;
  /** Element id whose text describes the dialog content. */
  ariaDescribedBy?: string;
  /** Width of the centered card — `sm` (auth confirms), `md` (default), `lg`. */
  size?: ModalSize;
  /** Extra classes for the inner `<Card>` (e.g. extra padding). */
  cardClassName?: string;
  /** Modal contents — typically `<Modal.Header>`, `<Modal.Body>`, `<Modal.Footer>`. */
  children: ReactNode;
}

/**
 * Structural modal primitive — backdrop + centered `<Card>` with focus trap,
 * ESC-to-close, and backdrop-click-to-close. Use the compound API for layout:
 *
 *   <Modal open onClose ariaLabel="Confirm delete">
 *     <Modal.Header>Title</Modal.Header>
 *     <Modal.Body>Are you sure?</Modal.Body>
 *     <Modal.Footer>
 *       <Button variant="ghost" onClick={onClose}>Cancel</Button>
 *       <Button variant="danger" onClick={onConfirm}>Delete</Button>
 *     </Modal.Footer>
 *   </Modal>
 *
 * Children can also be raw nodes if you need full control (e.g. a `motion`
 * wrapper for animated entry). The dialog has `role="dialog"` + `aria-modal`
 * so AT users get the right context.
 */
export function Modal({
  open,
  onClose,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  size = 'md',
  cardClassName = '',
  children,
}: ModalProps) {
  const trapRef = useFocusTrap(open);
  const autoLabelId = useId();

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm dark:bg-canvas-dark/70"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={trapRef}
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${SIZE[size]}`}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabelledBy ? undefined : ariaLabel}
        aria-labelledby={ariaLabelledBy ?? (ariaLabel ? undefined : autoLabelId)}
        aria-describedby={ariaDescribedBy}
      >
        <Card className={`p-6 sm:p-7 ${cardClassName}`}>{children}</Card>
      </div>
    </div>
  );
}

/**
 * Optional header slot — bold 18px heading with subtle bottom margin.
 * Pass `id` if you wire it to `<Modal ariaLabelledBy=...>`.
 */
function ModalHeader({
  children,
  className = '',
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <h2
      id={id}
      className={`text-lg font-semibold tracking-[-0.01em] text-slate-900 dark:text-white ${className}`}
    >
      {children}
    </h2>
  );
}

/** Body slot — natural reading text + top margin to separate from header. */
function ModalBody({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400 ${className}`}>
      {children}
    </div>
  );
}

/** Footer slot — right-aligned action row. Use `<Button>` instances inside. */
function ModalFooter({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`mt-6 flex flex-wrap items-center justify-end gap-3 ${className}`}>
      {children}
    </div>
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
